import type { AiThreadScope, ThreadListResponse } from "@repo/shared/types";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { listThreads } from "@/lib/api/ai-threads";

// Query-key family for thread lists. Shares the "ai-threads" namespace with the
// unread-count query (see chat-panel/sidebar). clientId is only meaningful for
// the "client" scope; it is normalized to null otherwise so the key is stable.
const threadsListKeys = {
  all: ["ai-threads", "list"] as const,
  list: (scope: AiThreadScope, clientId: string | null) =>
    ["ai-threads", "list", scope, clientId] as const,
};

// clientId only scopes the list in "client" mode.
function normalizeClientId(
  scope: AiThreadScope,
  clientId: string | null | undefined,
): string | null {
  return scope === "client" ? (clientId ?? null) : null;
}

/**
 * Managed thread-list query. Enabled only while the thread-list view is open.
 *
 * Retry policy: fail fast on 4xx client errors (no retry) — the platform serves
 * banks behind shared NATs with IP-keyed rate limiters on auth endpoints
 * (QP-92), so blind retry loops on 401/403 must be avoided. Network errors and
 * 5xx fall back to React Query's default retries (up to 3, exponential backoff).
 */
export function useThreadsQuery(
  mode: AiThreadScope,
  clientId: string | null | undefined,
  enabled: boolean,
) {
  const normalizedClientId = normalizeClientId(mode, clientId);
  const query = useQuery({
    queryKey: threadsListKeys.list(mode, normalizedClientId),
    queryFn: () =>
      listThreads({
        limit: 50,
        scope: mode,
        clientId: normalizedClientId ?? undefined,
      }),
    enabled,
  });
  return {
    threads: query.data?.threads ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

// ─── Cache-write helpers ─────────────────────────────────────────────────────
// One-for-one replacements for the chat panel's optimistic local-state writes.
// Each operates on the current scope's cached list with no network call, so
// today's instant-feedback UX is preserved.

function updateThreads(
  qc: QueryClient,
  mode: AiThreadScope,
  clientId: string | null | undefined,
  updater: (threads: ThreadListResponse["threads"]) => ThreadListResponse["threads"],
) {
  const key = threadsListKeys.list(mode, normalizeClientId(mode, clientId));
  qc.setQueryData<ThreadListResponse>(key, (old) =>
    old ? { ...old, threads: updater(old.threads) } : old,
  );
}

export function removeThreadFromCache(
  qc: QueryClient,
  mode: AiThreadScope,
  clientId: string | null | undefined,
  threadId: string,
) {
  updateThreads(qc, mode, clientId, (threads) => threads.filter((t) => t.id !== threadId));
}

export function markThreadReadInCache(
  qc: QueryClient,
  mode: AiThreadScope,
  clientId: string | null | undefined,
  threadId: string,
) {
  updateThreads(qc, mode, clientId, (threads) =>
    threads.map((t) => (t.id === threadId ? { ...t, unread: false } : t)),
  );
}

export function markAllThreadsReadInCache(
  qc: QueryClient,
  mode: AiThreadScope,
  clientId: string | null | undefined,
) {
  updateThreads(qc, mode, clientId, (threads) => threads.map((t) => ({ ...t, unread: false })));
}

export function invalidateThreadsList(
  qc: QueryClient,
  mode: AiThreadScope,
  clientId: string | null | undefined,
) {
  return qc.invalidateQueries({
    queryKey: threadsListKeys.list(mode, normalizeClientId(mode, clientId)),
  });
}
